import { useEffect } from "react"
import { useNavigate } from "react-router"
import {
    COMPANY_ROUTE,
    ERROR_ROUTE,
    LOGIN_ROUTE,
    MERCHANT_ROUTE,
    OFFICE_MANAGER_ROUTE,
    SERVICE_CENTER_ADMIN_ROUTE,
    SERVICE_CENTER_REPORTS_GUESTS_ROUTE,
    SERVICE_CENTER_REPORTS_ROUTE,
    SUPPLIER_ROUTE,
    SUPPORT_ROUTE,
    USER_ROUTE,
} from "../../../constants/routes"
import { useAppSelector } from "../../store/storeHooks"
import { LoginStatus, UserType } from "../../login/loginTypes"
import { selectLoginStatus, selectUserType } from "../../login/loginSelectors"
import { ErrorPageState } from "../../../core/router/pages/ErrorPage"
import { selectEffectiveUserType } from "../../context/contextSelectors"
import { useSearchParams } from "react-router-dom"
import { parseBool } from "../../../lib/lang"
import { useEnvironment } from "../../environment/envHooks"
import { EnvironmentType } from "../../environment/envTypes"

type RootProps = Readonly<{}>

const serviceCenterGuestsListRedirectList = [EnvironmentType.Prod, EnvironmentType.QA]
const serviceCenterDashboardRedirectList = [EnvironmentType.Test]

export function Root({}: RootProps) {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const loginStatus = useAppSelector(selectLoginStatus)
    const actualUserType = useAppSelector(selectUserType)
    const effectiveUserType = useAppSelector(selectEffectiveUserType)
    const { currentEnv } = useEnvironment()

    const asActualParam = searchParams.get("asActual") ?? ""
    const asActual = parseBool(asActualParam) ?? false
    const userType = asActual ? actualUserType : effectiveUserType

    useEffect(() => {
        if (loginStatus === LoginStatus.AUTHENTICATING) {
            navigate(LOGIN_ROUTE)
        } else {
            switch (userType) {
                case UserType.SUPPORT:
                    navigate(SUPPORT_ROUTE)
                    break
                case UserType.SUPPLIER:
                    navigate(SUPPLIER_ROUTE)
                    break
                case UserType.MERCHANT:
                    navigate(MERCHANT_ROUTE)
                    break
                case UserType.OFFICE_MANAGER:
                    navigate(OFFICE_MANAGER_ROUTE)
                    break
                case UserType.COMPANY:
                    navigate(COMPANY_ROUTE)
                    break
                case UserType.USER:
                    navigate(USER_ROUTE)
                    break
                case UserType.SERVICE_CENTER:
                    if (serviceCenterGuestsListRedirectList.includes(currentEnv.type)) {
                        navigate(SERVICE_CENTER_REPORTS_GUESTS_ROUTE)
                        break
                    }
                    if (serviceCenterDashboardRedirectList.includes(currentEnv.type)) {
                        navigate(SERVICE_CENTER_REPORTS_ROUTE)
                        break
                    }
                    navigate(SERVICE_CENTER_ADMIN_ROUTE)
                    break
                default:
                    const navState: ErrorPageState = {
                        title: "Unknown user type",
                        message: `User type: ${userType}`,
                    }
                    navigate(ERROR_ROUTE, { state: navState })
            }
        }
    }, [loginStatus, userType])

    return null
}
